import React, { useEffect, useState } from 'react'
import MainAreaWrapper from '../layout/MainAreaWrapper'
import { Box, MenuItem, Select, Tab, Tabs } from '@mui/material'
import { OverallSummaryCard } from '../components/vital-reports/overall-summary/overall-card'
import AllTable from '../components/vital-reports/table/all-table'
import SleepChart from '../components/vital-reports/overall-summary/sleep-chart'
import JournalChart from '../components/vital-reports/overall-summary/journal-chart'
import CategoryWiseChart from '../components/vital-reports/overall-summary/category-wise-chart'

import {
  allHeadCells,
  importantHeadCells,
  activitiesHeadCells,
  observationHeadCells,
  vitalsHeadCells
} from '../components/vital-reports/table/data'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment'
import moment from 'moment'
import { usePatient } from "../context/PatientContext";
import { useFormik } from "formik";
import * as API from "../HTTPS/VitalApis";
import { set } from 'rsuite/esm/internals/utils/date'

const VitalReports = () => {
  const [tab, setTab] = useState(0)
  const [recipient, setRecipient] = useState('Care Recipient')
  const [date, setDate] = useState(moment())
  const [vitalSummary, setVitalSummary] = useState({});
  const [allRows, setAllRows] = useState([]);
  const [importantRows, setImportantRows] = useState([]);
  const [activitiesRows, setActivitiesRows] = useState([]);
  const [observationRows, setObservationRows] = useState([]);
  const [vitalsRows, setVitalsRows] = useState([]);

  const [sleepdayData, setSleepdayData] = useState([]);
  const [respiratoryRateData, setRespiratoryRateData] = useState([]);
  const [heartrateData, setHeartrateData] = useState([]);
  const [bloodOxygenData, setBloodOxygenData] = useState([]);
  const [bloodPressureData, setBloodPressureData] = useState([]);
  const [temperatureData, setTemperatureData] = useState([]);
  const [weightData, setWeightData] = useState([]);

  const [groupedJr, setGroupedJr] = useState([]);
  const [groupedAct, setGroupedAct] = useState([]);
  const [groupedObs, setGroupedObs] = useState([]);
  const [groupedJrImp, setGroupedJrImp] = useState([]);
  const [groupedVits, setGroupedVits] = useState([]);

  const {
    patient,
    patientList,
    setPatient,
    setPatientList,
  } = usePatient();

  const initialDateRef = React.useRef(moment());

  const formik = useFormik({
    initialValues: {
      recipient: patientList.length > 0 ? patientList[0].id : "",
      selDate: initialDateRef.current, // today
    },
    enableReinitialize: true,  // <--- important
    onSubmit: async (values) => {
      if (values.recipient === "") return;

      const { fromDate, toDate } = getDateRange();

      // var result = await API.fetchVitalSummarySnapshot(fromDate, toDate, values.recipient);
      // setVitalSummary(result);

      var journalResult = await API.fetchJournalInsigthsSnapshot(fromDate, toDate, values.recipient);
      console.log("journalResult", journalResult);

      if (journalResult.length > 0) {
        setAllRows(journalResult);
        setImportantRows(journalResult.filter(item => item.isImp));
        setActivitiesRows(journalResult.filter(item => item.type === "Activity"));
        setObservationRows(journalResult.filter(item => item.type === "Observation"));
        setVitalsRows(journalResult.filter(item => item.type === "Vitals"));
        var groupedJr = getEventCounts(journalResult);
        setGroupedJr(groupedJr);

        var groupedJrImp = getEventCounts(journalResult.filter(item => item.isImp));
        setGroupedJrImp(groupedJrImp);

        var groupedAct = groupActivityByCategory(journalResult.filter(item => item.type === "Activity"));
        setGroupedAct(groupedAct);

        var groupedObs = groupActivityByCategory(journalResult.filter(item => item.type === "Observation"));
        setGroupedObs(groupedObs);

        var groupedVit = groupActivityByCategory(journalResult.filter(item => item.type === "Vitals"));
        setGroupedVits(groupedVit);

      } else {
        setAllRows([]);
        setImportantRows([]);
        setActivitiesRows([]);
        setObservationRows([]);
        setVitalsRows([]);
        setGroupedJr([]);
      }

      fetchSleepTotal();
    },
  });

  function getEventCounts(events) {
    const grouped = {};

    events.forEach((event) => {
      const day = moment(event.date).format("YYYY-MM-DD");

      if (!grouped[day]) {
        grouped[day] = { date: day, Activity: 0, Observation: 0, Vitals: 0 };
      }

      if (grouped[day][event.type] !== undefined) {
        grouped[day][event.type] += 1;
      }
    });

    return Object.values(grouped).sort((a, b) => new Date(a.date) - new Date(b.date));
  }

  function groupActivityByCategory(events) {
    const grouped = Object.values(
      events.reduce((acc, { date, category }) => {
        const day = moment(date).format("YYYY-MM-DD");

        if (!acc[day]) acc[day] = { day };
        acc[day][category] = (acc[day][category] || 0) + 1;
        return acc;
      }, {})
    );

    return grouped;
  }

  // when patientList updates, reinit picks it up
  useEffect(() => {
    if (patientList.length > 0 && !formik.values.recipient) {
      formik.setFieldValue("recipient", patientList[0].id, false);
      // optional auto-submit right after initializing
      setTimeout(() => {
        formik.submitForm();
      }, 100); // wait for formik to set value
    }
  }, [patientList]);

  React.useEffect(() => {
    formik.submitForm();
  }, []);


  async function fetchSleepTotal() {

    if (formik.values.recipient === "") return;

    const { fromDate, toDate } = getDateRange();

    var sleepdayData = await API.fetchDayWiseSleepTotal(fromDate, toDate, formik.values.recipient);
    setSleepdayData(sleepdayData);
  }

  async function fetchRespiratoryRateTotal() {

    if (formik.values.recipient === "") return;

    const { fromDate, toDate } = getDateRange();

    var respiratoryRateData = await API.fetchDayWiseRespiratoryRateTotal(fromDate, toDate, formik.values.recipient);
    setRespiratoryRateData(respiratoryRateData);
  }

  async function fetchHeartrateTotal() {

    if (formik.values.recipient === "") return;

    const { fromDate, toDate } = getDateRange();

    var heartrateData = await API.fetchDayWiseHeartrateTotal(fromDate, toDate, formik.values.recipient);
    setHeartrateData(heartrateData);
  }

  async function fetchBloodOxygenTotal() {

    if (formik.values.recipient === "") return;

    const { fromDate, toDate } = getDateRange();

    var bloodOxygenData = await API.fetchDayWiseBloodOxygenTotal(fromDate, toDate, formik.values.recipient);
    setBloodOxygenData(bloodOxygenData);
  }

  async function fetchBloodPressureTotal() {

    if (formik.values.recipient === "") return;

    const { fromDate, toDate } = getDateRange();

    var bloodPressureData = await API.fetchDayWiseBloodPressureTotal(fromDate, toDate, formik.values.recipient);
    setBloodPressureData(bloodPressureData);
  }

  async function fetchTemperatureTotal() {

    if (formik.values.recipient === "") return;

    const { fromDate, toDate } = getDateRange();

    var temperatureData = await API.fetchDayWiseTemperatureTotal(fromDate, toDate, formik.values.recipient);
    setTemperatureData(temperatureData);
  }

  async function fetchWeightTotal() {

    if (formik.values.recipient === "") return;

    const { fromDate, toDate } = getDateRange();

    var weightData = await API.fetchDayWiseWeightTotal(fromDate, toDate, formik.values.recipient);
    setWeightData(weightData);
  }

  function getDateRange() {
    const toDate = formik.values.selDate.format("YYYY-MM-DD");
    const fromDate = formik.values.selDate.clone().subtract(6, "days").format("YYYY-MM-DD");

    return { fromDate, toDate };
  }

  return (
    <MainAreaWrapper>
      <LocalizationProvider dateAdapter={AdapterMoment}>
        <main className="bg-white mt-28 lg:mt-10 rounded-t-3xl pb-10 lg:pb-0 -z-0">
          <div className="mx-auto p-4 md:p-6 2xl:p-10">
            <div className="xl:p-6 bg-gray-50 min-h-screen">
              {/* Top Filters */}
              <div className="flex justify-between mb-4 gap-4">
                {/* Select */}
                <Select
                  name="recipient"
                  value={formik.values.recipient}
                  onChange={(e) => {
                    formik.setFieldValue("recipient", e.target.value);
                    formik.submitForm();
                  }}
                  className="bg-white rounded-lg shadow-sm w-full"
                >
                  {patientList.map((item) => (
                    <MenuItem key={item.id} value={item.id}>
                      {`${item.fname} ${item.lname}`}
                    </MenuItem>
                  ))}
                </Select>

                {/* Date Picker */}
                <DatePicker
                  label="Pick a date"
                  value={formik.values.selDate}
                  onChange={(val) => {
                    console.log("Picker value:", val, "isMoment:", moment.isMoment(val));
                    formik.setFieldValue("selDate", val);
                    formik.submitForm();
                  }}
                  slotProps={{ textField: { fullWidth: true } }}
                  disableFuture
                />
              </div>

              <OverallSummaryCard
                vitalSummary={vitalSummary}
                selDate={formik.values.selDate}
                onCardClick={(item, index) => {
                  if (item.label == 'Sleep') {
                    fetchSleepTotal();
                  }
                  else if (item.label == 'Breathing Rate') {
                    fetchRespiratoryRateTotal();
                  }
                  else if (item.label == 'Heart Rate') {
                    fetchHeartrateTotal();
                  }
                  else if (item.label == 'SO₂ Level') {
                    fetchBloodOxygenTotal();
                  }
                  else if (item.label == 'Blood Pressure') {
                    fetchBloodPressureTotal();
                  }
                  else if (item.label == 'Temperature') {
                    fetchTemperatureTotal();
                  }
                  else if (item.label == 'Weight') {
                    fetchWeightTotal();
                  }
                }}
                sleepData={sleepdayData}
                respiratoryRateData={respiratoryRateData}
                heartrateData={heartrateData}
                bloodOxygenData={bloodOxygenData}
                bloodPressureData={bloodPressureData}
                temperatureData={temperatureData}
                weightData={weightData} />

              {/* Tabs */}
              <Box
                sx={{ borderBottom: 1, borderColor: 'divider' }}
                className="my-7"
              >
                <Tabs
                  value={tab}
                  onChange={(_, newValue) => setTab(newValue)}
                  centered
                  className="!w-full"
                  TabIndicatorProps={{ style: { display: 'none' } }} // hide default underline
                >
                  <Tab
                    label="All"
                    className="!flex-grow !w-20 !rounded-t-xl !font-semibold !text-[10px] 2xl:!text-base"
                    sx={{
                      bgcolor: '#fff',
                      border: '2px solid transparent',
                      '&.Mui-selected': {
                        bgcolor: '#ECF1F4',
                        borderBottom: '4px solid #1F456E',
                      },
                    }}
                  />
                  <Tab
                    label="Important"
                    className="!flex-grow !w-20 !rounded-t-xl !font-semibold !text-[10px] 2xl:!text-base"
                    sx={{
                      bgcolor: '#fff',
                      border: '2px solid transparent',
                      '&.Mui-selected': {
                        bgcolor: '#ECF1F4',
                        borderBottom: '4px solid #1F456E',
                      },
                    }}
                  />
                  <Tab
                    label="Observations"
                    className="!flex-grow !w-20 !rounded-t-xl !font-semibold !text-[10px] 2xl:!text-base"
                    sx={{
                      bgcolor: '#fff',
                      border: '2px solid transparent',
                      '&.Mui-selected': {
                        bgcolor: '#ECF1F4',
                        borderBottom: '4px solid #1F456E',
                      },
                    }}
                  />
                  <Tab
                    label="Activities"
                    className="!flex-grow !w-20 !rounded-t-xl !font-semibold !text-[10px] 2xl:!text-base"
                    sx={{
                      bgcolor: '#fff',
                      border: '2px solid transparent',
                      '&.Mui-selected': {
                        bgcolor: '#ECF1F4',
                        borderBottom: '4px solid #1F456E',
                      },
                    }}
                  />
                  <Tab
                    label="Vitals"
                    className="!flex-grow !w-20 !rounded-t-xl !font-semibold !text-[10px] 2xl:!text-base"
                    sx={{
                      bgcolor: '#fff',
                      border: '2px solid transparent',
                      '&.Mui-selected': {
                        bgcolor: '#ECF1F4',
                        borderBottom: '4px solid #1F456E',
                      },
                    }}
                  />
                </Tabs>
              </Box>
              {/* Tab Content */}
              {tab === 0 && (
                <div className="flex gap-4 flex-wrap xl:flex-nowrap">
                  <AllTable headCells={allHeadCells} rows={allRows} />
                  <div className="!bg-white p-2 shadow !h-full w-full">
                    <JournalChart events={groupedJr} chartTtile={'All Observations & Activities'} />
                  </div>
                </div>
              )}
              {tab === 1 && (
                <div className="flex gap-4 flex-wrap xl:flex-nowrap">
                  <AllTable
                    headCells={importantHeadCells}
                    rows={importantRows}
                  />
                  <div className="!bg-white p-2 shadow !h-full w-full">
                    <JournalChart events={groupedJrImp} chartTtile={'Important Observations & Activities'} />
                  </div>
                </div>
              )}
              {tab === 2 && (
                <div className="flex gap-4 flex-wrap xl:flex-nowrap">
                  <AllTable
                    headCells={observationHeadCells}
                    rows={observationRows}
                  />
                  <div className="!bg-white p-2 shadow !h-full w-full">
                    <CategoryWiseChart data={groupedObs} chartTitle={'Observation Chart'} />
                  </div>
                </div>
              )}
              {tab === 3 && (
                <div className="flex gap-4 flex-wrap xl:flex-nowrap">
                  <AllTable
                    headCells={activitiesHeadCells}
                    rows={activitiesRows}
                  />
                  <div className="!bg-white p-2 shadow !h-full w-full">
                    <CategoryWiseChart data={groupedAct} chartTitle={'Activity Chart'} />
                  </div>
                </div>
              )}
              {tab === 4 && (
                <div className="flex gap-4 flex-wrap xl:flex-nowrap">
                  <AllTable
                    headCells={vitalsHeadCells}
                    rows={vitalsRows}
                  />
                  <div className="!bg-white p-2 shadow !h-full w-full">
                    <CategoryWiseChart data={groupedVits} chartTitle={'Vital Chart'} />
                  </div>
                </div>
              )}
            </div>
          </div>
        </main>
      </LocalizationProvider>
    </MainAreaWrapper>
  )
}

export default VitalReports
